import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { Pagination } from '@/components/shared/Pagination';
import { EmptyState } from '@/components/shared/EmptyState';
import type { BulkImportRowListItem } from '@/services/bulk-import.service';
import { CLASSIFICATION_BADGE, CLASSIFICATION_LABELS } from './bulk-import-ui.constants';

interface BulkImportRowTableProps {
  rows: BulkImportRowListItem[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  selectedIds: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleAllOnPage: (checked: boolean) => void;
  onOpenRow: (row: BulkImportRowListItem) => void;
}

export function BulkImportRowTable({
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  selectedIds,
  onToggleRow,
  onToggleAllOnPage,
  onOpenRow,
}: BulkImportRowTableProps) {
  if (total === 0) {
    return <EmptyState title="No rows match the current filter" />;
  }

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left">
              <th className="w-10 px-4 py-3">
                <input
                  type="checkbox"
                  aria-label="Select all rows on this page"
                  checked={allOnPageSelected}
                  onChange={(e) => onToggleAllOnPage(e.target.checked)}
                />
              </th>
              <th className="px-4 py-3 font-medium text-foreground">Row #</th>
              <th className="px-4 py-3 font-medium text-foreground">Serial / Asset</th>
              <th className="px-4 py-3 font-medium text-foreground">Equipment Name</th>
              <th className="px-4 py-3 font-medium text-foreground">Classification</th>
              <th className="px-4 py-3 font-medium text-foreground">Issues</th>
              <th className="px-4 py-3 font-medium text-foreground">Corrections</th>
              <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    aria-label={`Select row ${row.rowNumber}`}
                    checked={selectedIds.has(row.id)}
                    onChange={() => onToggleRow(row.id)}
                  />
                </td>
                <td className="px-4 py-3 font-mono text-muted-foreground">{row.rowNumber}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-0.5">
                    <span>{row.serialNumber ?? '—'}</span>
                    {row.assetNumber && (
                      <span className="text-xs text-muted-foreground">{row.assetNumber}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{row.equipmentName ?? '—'}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${CLASSIFICATION_BADGE[row.classification]}`}
                  >
                    {CLASSIFICATION_LABELS[row.classification]}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {row.issueCount > 0 ? row.issueCount : '—'}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {row.correctionCount > 0 ? row.correctionCount : '—'}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onOpenRow(row)}
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    title="View row detail"
                    aria-label={`View detail for row ${row.rowNumber}`}
                  >
                    <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={pageSize} total={total} onPageChange={onPageChange} />
    </div>
  );
}
